/**
 * Toast Notification Utilities
 *
 * Centralized toast management with consistent styling and behavior.
 * Built on top of Sonner for modern toast notifications.
 */

import { toast } from 'sonner';

interface ToastOptions {
  /** Toast duration in milliseconds */
  duration?: number;
  /** Whether to show a close button */
  closeButton?: boolean;
  /** Custom action button */
  action?: {
    label: string;
    onClick: () => void;
  };
  /** Position for this specific toast */
  position?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
}

/**
 * Toast notification utilities with consistent styling
 */
export const toastUtils = {
  /**
   * Show a success toast
   */
  success: (message: string, description?: string, options?: ToastOptions) => {
    return toast.success(message, {
      description,
      duration: options?.duration || 4000,
      closeButton: options?.closeButton,
      action: options?.action,
      position: options?.position,
    });
  },

  /**
   * Show an error toast
   */
  error: (message: string, description?: string, options?: ToastOptions) => {
    return toast.error(message, {
      description,
      duration: options?.duration || 6000, // Longer duration for errors
      closeButton: options?.closeButton ?? true, // Default to showing close button for errors
      action: options?.action,
      position: options?.position,
    });
  },

  /**
   * Show a warning toast
   */
  warning: (message: string, description?: string, options?: ToastOptions) => {
    return toast.warning(message, {
      description,
      duration: options?.duration || 5000,
      closeButton: options?.closeButton,
      action: options?.action,
      position: options?.position,
    });
  },

  /**
   * Show an info toast
   */
  info: (message: string, description?: string, options?: ToastOptions) => {
    return toast.info(message, {
      description,
      duration: options?.duration || 4000,
      closeButton: options?.closeButton,
      action: options?.action,
      position: options?.position,
    });
  },

  /**
   * Show a loading toast that can be updated later
   */
  loading: (message: string, description?: string) => {
    return toast.loading(message, {
      description,
    });
  },

  /**
   * Show a custom toast with full control
   */
  custom: (content: React.ReactNode, options?: ToastOptions) => {
    return toast.custom(() => content as React.ReactElement, {
      duration: options?.duration || 4000,
      closeButton: options?.closeButton,
      position: options?.position,
    });
  },

  /**
   * Update an existing toast (useful for loading states)
   */
  update: (toastId: string | number, type: 'success' | 'error' | 'warning' | 'info', message: string, description?: string) => {
    switch (type) {
      case 'success':
        return toast.success(message, { id: toastId, description });
      case 'error':
        return toast.error(message, { id: toastId, description });
      case 'warning':
        return toast.warning(message, { id: toastId, description });
      case 'info':
        return toast.info(message, { id: toastId, description });
    }
  },

  /**
   * Dismiss a specific toast
   */
  dismiss: (toastId?: string | number) => {
    if (toastId) {
      toast.dismiss(toastId);
    } else {
      toast.dismiss();
    }
  },

  /**
   * Dismiss all toasts
   */
  dismissAll: () => {
    toast.dismiss();
  },
};

/**
 * Progress toast utilities for long-running operations
 */
export type OperationToastType = 'import' | 'bulk' | 'recurring';

interface ProgressToastStats {
  processed: number;
  total: number;
  timeElapsed?: string;
}

export const progressToast = {
  /**
   * Start a progress toast for a new operation
   * Returns the toast ID for future updates
   */
  start: (
    id: string,
    title: string,
    type: OperationToastType = 'bulk',
    description?: string
  ): string => {
    toast.loading(title, {
      id,
      description: description || 'Starting...',
      duration: Infinity, // Don't auto-dismiss
    });
    return id;
  },

  /**
   * Update progress on an existing toast
   */
  update: (
    id: string,
    stats: ProgressToastStats,
    title?: string
  ): void => {
    const percentage = stats.total > 0
      ? Math.round((stats.processed / stats.total) * 100)
      : 0;

    const description = `${stats.processed.toLocaleString()} of ${stats.total.toLocaleString()} items (${percentage}%)`;

    toast.loading(title || 'Processing...', {
      id,
      description,
      duration: Infinity,
    });
  },

  /**
   * Complete a progress toast with success
   */
  complete: (
    id: string,
    title: string,
    stats?: { processed: number; time?: string },
    action?: { label: string; onClick: () => void }
  ): void => {
    const description = stats
      ? `${stats.processed.toLocaleString()} items${stats.time ? ` in ${stats.time}` : ''}`
      : 'Operation completed successfully';

    toast.success(title, {
      id,
      description,
      duration: 5000,
      action: action ? {
        label: action.label,
        onClick: action.onClick,
      } : undefined,
    });
  },

  /**
   * Complete a progress toast with failure
   */
  fail: (
    id: string,
    title: string,
    error?: string,
    action?: { label: string; onClick: () => void }
  ): void => {
    toast.error(title, {
      id,
      description: error || 'An error occurred',
      duration: 8000,
      action: action ? {
        label: action.label,
        onClick: action.onClick,
      } : undefined,
    });
  },

  /**
   * Dismiss a progress toast
   */
  dismiss: (id: string): void => {
    toast.dismiss(id);
  },
};

/**
 * Operation-specific toast utilities
 */
export const operationToasts = {
  /**
   * Toast for operation start
   */
  started: (operationType: string, operationName?: string) => {
    return toastUtils.info(
      'Operation Started',
      `${operationName || operationType} is now processing...`,
      {
        duration: 3000,
        action: {
          label: 'View Progress',
          onClick: () => {
            // TODO: Navigate to progress view
          }
        }
      }
    );
  },

  /**
   * Toast for operation completion
   */
  completed: (operationType: string, operationName?: string, stats?: { processed: number; time: string }) => {
    const description = stats
      ? `Processed ${stats.processed.toLocaleString()} items in ${stats.time}`
      : `${operationName || operationType} completed successfully`;

    return toastUtils.success(
      'Operation Completed',
      description,
      {
        duration: 5000,
        action: {
          label: 'View Results',
          onClick: () => {
            // TODO: Navigate to results view
          }
        }
      }
    );
  },

  /**
   * Toast for operation failure
   */
  failed: (operationType: string, operationName?: string, error?: string) => {
    return toastUtils.error(
      'Operation Failed',
      error || `${operationName || operationType} encountered an error`,
      {
        duration: 8000,
        closeButton: true,
        action: {
          label: 'View Logs',
          onClick: () => {
            // TODO: Navigate to error logs
          }
        }
      }
    );
  },

  /**
   * Toast for operation progress milestones
   */
  milestone: (percentage: number, message?: string) => {
    return toastUtils.info(
      `${percentage}% Complete`,
      message || 'Operation is progressing...',
      {
        duration: 2000,
      }
    );
  },

  /**
   * Toast for CSV import specific events
   */
  csvImport: {
    validationComplete: (rowCount: number, errors: number) => {
      if (errors === 0) {
        return toastUtils.success(
          'CSV Validation Complete',
          `${rowCount.toLocaleString()} rows validated successfully`
        );
      } else {
        return toastUtils.warning(
          'CSV Validation Complete',
          `${rowCount.toLocaleString()} rows validated, ${errors} errors found`,
          {
            action: {
              label: 'View Errors',
              onClick: () => { /* TODO: Show validation errors */ }
            }
          }
        );
      }
    },

    mappingComplete: (mappedColumns: number, totalColumns: number) => {
      return toastUtils.info(
        'Column Mapping Complete',
        `${mappedColumns}/${totalColumns} columns mapped automatically`
      );
    },

    templateSaved: (templateName: string) => {
      return toastUtils.success(
        'Template Saved',
        `"${templateName}" can be reused for future imports`
      );
    },
  },

  /**
   * Toast for connection status
   */
  connection: {
    connected: () => {
      return toastUtils.success(
        'Real-time Connected',
        'Live updates are now available',
        { duration: 2000 }
      );
    },

    disconnected: () => {
      return toastUtils.warning(
        'Real-time Disconnected',
        'Switching to polling updates',
        { duration: 3000 }
      );
    },

    reconnected: () => {
      return toastUtils.info(
        'Real-time Reconnected',
        'Live updates restored',
        { duration: 2000 }
      );
    },
  },
};

/**
 * Export the main toast function for direct use
 */
export { toast };

/**
 * Default export for convenience
 */
export default toastUtils;